import dayjs from "dayjs";
import { Redo2, Undo2 } from "lucide-react";
import React, { useMemo, useState } from "react";

import { randomUuid } from "@/utils/string.utils";
import ShiftPlanner from "./ShiftPlanner";
import { ShiftPlannerConsultant, ShiftPlannerShift } from "./shiftPlanner.types";
import { diffShifts } from "./shiftPlanner.utils";
import { useShiftPlannerHistory } from "./useShiftPlannerHistory";

// Employee numbers on a few consultants exercise the context-menu search
// (matches on name OR number)
const consultants: ShiftPlannerConsultant[] = [
	{ id: "c1", name: "Joel Söderkvist", employeeNumber: 101 },
	{ id: "c2", name: "Veronica Nilsson", employeeNumber: 102 },
	{ id: "c3", name: "Nils Kylberg" },
	{ id: "c4", name: "Torsten Junestrand" },
	{ id: "c5", name: "Erik Enochson", employeeNumber: 117 },
	{ id: "c6", name: "Kim Popova" },
	{ id: "c7", name: "Viktoria Olsson" },
	{ id: "c8", name: "Fredrik Andersson" },
	{ id: "c9", name: "Molly Berg" },
	{ id: "c10", name: "Julia Kaufeldt" },
	{ id: "c11", name: "Mats Nyberg" },
	{ id: "c12", name: "Nemi Andersson" },
	{ id: "c13", name: "Sophie Eliasson" },
];

function buildInitialShifts(): ShiftPlannerShift[] {
	const monday = dayjs().startOf("isoWeek");
	const date = (week: number, day: number) =>
		monday.add(week, "week").add(day, "day").format("YYYY-MM-DD");

	const standard = { startTime: "10:00", endTime: "14:00", breakHours: 1 };

	return [
		// Week 1
		{ id: randomUuid(), date: date(0, 0), consultantId: "c1", ...standard },
		{
			id: randomUuid(),
			date: date(0, 1),
			consultantId: "c1",
			startTime: "10:00",
			endTime: "14:00",
		},
		{ id: randomUuid(), date: date(0, 4), consultantId: "c2", ...standard },
		{
			id: randomUuid(),
			date: date(0, 0),
			consultantId: "c3",
			startTime: "09:00",
			endTime: "15:00",
		},
		{
			id: randomUuid(),
			date: date(0, 1),
			consultantId: "c3",
			startTime: "11:00",
			endTime: "15:30",
		},
		{ id: randomUuid(), date: date(0, 0), consultantId: "c4", ...standard },
		{
			id: randomUuid(),
			date: date(0, 1),
			consultantId: "c4",
			startTime: "08:00",
			endTime: "12:00",
		},
		{ id: randomUuid(), date: date(0, 4), consultantId: "c5", ...standard, absence: true },
		{
			id: randomUuid(),
			date: date(0, 3),
			consultantId: "c6",
			startTime: "10:00",
			endTime: "14:00",
		},
		{ id: randomUuid(), date: date(0, 4), consultantId: "c6", ...standard },
		{ id: randomUuid(), date: date(0, 0), consultantId: "c7", ...standard },
		{
			id: randomUuid(),
			date: date(0, 1),
			consultantId: "c7",
			startTime: "10:00",
			endTime: "14:00",
		},
		{ id: randomUuid(), date: date(0, 0), consultantId: "c8", ...standard },
		{
			id: randomUuid(),
			date: date(0, 0),
			consultantId: "c9",
			startTime: "10:00",
			endTime: "14:00",
		},
		{
			id: randomUuid(),
			date: date(0, 3),
			consultantId: "c9",
			startTime: "10:00",
			endTime: "14:00",
		},
		{ id: randomUuid(), date: date(0, 0), consultantId: "c10", ...standard },
		{
			id: randomUuid(),
			date: date(0, 3),
			consultantId: "c10",
			startTime: "10:00",
			endTime: "14:00",
		},
		{ id: randomUuid(), date: date(0, 0), consultantId: "c11", ...standard },
		{ id: randomUuid(), date: date(0, 1), consultantId: "c12", ...standard },
		{ id: randomUuid(), date: date(0, 2), consultantId: "c13", ...standard },
		// Unassigned with pending applications/invitations
		{
			id: randomUuid(),
			date: date(0, 1),
			startTime: "10:00",
			endTime: "14:00",
			breakHours: 1,
			appliedConsultantNames: ["Mats Nyberg", "Nemi Andersson", "Sophie Eliasson"],
		},
		{
			id: randomUuid(),
			date: date(0, 4),
			startTime: "10:00",
			endTime: "14:00",
			invitedConsultantNames: ["Kim Popova"],
			// With the id the shift renders as an "Önskad" pill on Kim's row
			// instead of in the unassigned block
			invitedConsultantIds: ["c6"],
		},
		// Week 2
		{ id: randomUuid(), date: date(1, 0), consultantId: "c1", ...standard },
		{
			id: randomUuid(),
			date: date(1, 1),
			consultantId: "c1",
			startTime: "10:00",
			endTime: "14:00",
		},
		{ id: randomUuid(), date: date(1, 4), consultantId: "c2", ...standard },
	];
}

/**
 * The ShiftPlanner grid on its own — no page chrome. The component is fully
 * controlled, so this example keeps the shifts array in local state to make
 * editing work (pills, popover with "Klar", shift+click/drag slot multi-select).
 * Parent hook-in points are demoed with alerts: `onRequestConsultantPicker`
 * is where the real page opens the consultant picker/drawer. Everything else
 * (title, Dagar/Timmar summary via the exported plannerSummary, "Rensa alla
 * pass", save bar) belongs to the integrating page, not the component.
 * Weeks render in a scrollable area that starts focused on the last shift
 * week and extends when scrolled near an edge.
 *
 * The dashed bar above the grid is a demo of that parent-side save lifecycle,
 * built from the exported helpers: `useShiftPlannerHistory` gives gesture-level
 * undo/redo of the draft, and `diffShifts` (draft vs last saved state) drives
 * the live "N skapas / ändras / tas bort" preview and the save confirmation.
 */
const ShiftPlannerExample: React.FC = () => {
	const initial = useMemo(buildInitialShifts, []);
	const [savedShifts, setSavedShifts] = useState<ShiftPlannerShift[]>(initial);
	const { shifts, setShifts, undo, redo, canUndo, canRedo, reset } =
		useShiftPlannerHistory(initial);

	const diff = diffShifts(savedShifts, shifts);
	const changeCount = diff.created.length + diff.updated.length + diff.removed.length;

	function handleSave() {
		window.alert(
			`Parent hook: save\n\nHär sparar sidan ändringarna till servern:\n` +
				`• ${diff.created.length} pass skapas\n` +
				`• ${diff.updated.length} pass ändras\n` +
				`• ${diff.removed.length} pass tas bort`
		);
		setSavedShifts(shifts);
		reset(shifts);
	}

	const historyButtonClasses =
		"flex h-8 w-8 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white";

	return (
		<div className="mx-auto max-w-6xl p-6">
			<div className="mb-4 flex items-center gap-3 rounded border border-dashed border-gray-400 bg-gray-50 px-3 py-2">
				<span className="text-xs font-semibold uppercase text-gray-500">
					Förälderns chrome (demo)
				</span>
				<button
					type="button"
					aria-label="Ångra"
					title="Ångra"
					disabled={!canUndo}
					onClick={undo}
					className={historyButtonClasses}
				>
					<Undo2 className="h-4 w-4" />
				</button>
				<button
					type="button"
					aria-label="Gör om"
					title="Gör om"
					disabled={!canRedo}
					onClick={redo}
					className={historyButtonClasses}
				>
					<Redo2 className="h-4 w-4" />
				</button>
				<span className="text-sm text-gray-700">
					{changeCount === 0
						? "Inga osparade ändringar"
						: `${diff.created.length} nya pass · ${diff.updated.length} ändrade · ${diff.removed.length} borttagna`}
				</span>
				<button
					type="button"
					disabled={changeCount === 0}
					onClick={handleSave}
					className="ml-auto rounded bg-success px-4 py-1.5 text-sm font-semibold text-white hover:bg-success/90 disabled:opacity-40"
				>
					Spara
				</button>
			</div>

			<ShiftPlanner
				shifts={shifts}
				consultants={consultants}
				onChange={setShifts}
				onRequestConsultantPicker={(shiftIds) =>
					window.alert(
						`Parent hook: onRequestConsultantPicker\n\nHär öppnar sidan konsultväljaren för ${
							shiftIds.length
						} pass:\n${shiftIds.join("\n")}`
					)
				}
				// The right-click menu on open shifts — the real page feeds this
				// from useMatchingConsultants and books/invites via its draft
				matchingConsultants={consultants}
				onAssignConsultant={(consultant, shiftIds) =>
					setShifts(
						shifts.map((shift) =>
							shiftIds.includes(shift.id)
								? {
										...shift,
										consultantId: consultant.id,
										invitedConsultantIds: [],
										invitedConsultantNames: [],
								  }
								: shift
						)
					)
				}
				onInviteConsultant={(consultant, shiftIds) =>
					setShifts(
						shifts.map((shift) =>
							shiftIds.includes(shift.id) && !shift.invitedConsultantIds?.includes(consultant.id)
								? {
										...shift,
										invitedConsultantIds: [...(shift.invitedConsultantIds ?? []), consultant.id],
										invitedConsultantNames: [
											...(shift.invitedConsultantNames ?? []),
											consultant.name,
										],
								  }
								: shift
						)
					)
				}
			/>
		</div>
	);
};

export default ShiftPlannerExample;
